/**
 * Storage: media files nothing uses, and bucket objects nothing tracks.
 *
 * Read-only on purpose. This is the evidence any deletion would have to rest on: the existing
 * manual cleanup deletes every bucket object without a media record on sight, and some features
 * write objects without one, so nothing should be removed until these numbers are trusted.
 *
 * The Admin API builds the report in the background and keeps it in memory, so a deploy or
 * restart clears it — run it again.
 */

import { useState } from 'react'
import { Alert, Box, Button, LinearProgress, Typography } from '@mui/material'
import { Assessment } from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { PageShell } from '@components/common/PageShell'
import { ResponsiveTabs, type ResponsiveTabItem } from '@components/common/ResponsiveTabs'
import { storageReportApi } from '@api/endpoints/storageReport'
import { ReportSummary } from '../components/storage/ReportSummary'
import { ReportFilesTable } from '../components/storage/ReportFilesTable'
import { UntrackedObjectsTable } from '../components/storage/UntrackedObjectsTable'
import { ReferenceSourcesPanel } from '../components/storage/ReferenceSourcesPanel'
import { STORAGE_REPORT_KEY, apiErrorMessage, formatDateTime } from '../components/storage/storageFormat'

const TABS: ResponsiveTabItem[] = [
  { key: 'unused', label: 'Unused' },
  { key: 'library', label: 'Library only' },
  { key: 'untracked', label: 'Untracked objects' },
  { key: 'missing', label: 'Missing files' },
  { key: 'sources', label: 'Sources checked' },
]

const STATUS_KEY = [...STORAGE_REPORT_KEY, 'status']

export default function AdminStoragePage() {
  const queryClient = useQueryClient()
  const [tab, setTab] = useState('unused')

  const status = useQuery({
    queryKey: STATUS_KEY,
    queryFn: storageReportApi.status,
    // Poll only while a report is being built.
    refetchInterval: (q) => (q.state.data?.isRunning ? 3000 : false),
  })

  const run = useMutation({
    mutationFn: storageReportApi.run,
    onSuccess: (next) => queryClient.setQueryData(STATUS_KEY, next),
  })

  const data = status.data
  const summary = data?.summary ?? null
  const isRunning = data?.isRunning ?? false
  const reportKey = summary?.generatedAt ?? 'none'

  return (
    <PageShell
      title="Storage"
      subtitle="Media files nothing uses and bucket objects nothing tracks. Read-only: nothing on this page deletes anything."
      actions={
        <Button
          variant="contained"
          startIcon={<Assessment />}
          onClick={() => run.mutate()}
          disabled={isRunning || run.isPending || status.isLoading}
        >
          {isRunning ? 'Running…' : summary ? 'Run again' : 'Run report'}
        </Button>
      }
    >
      {isRunning && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
          <Typography variant="caption" color="text.secondary">
            Reading every media record and listing the bucket. Started {formatDateTime(data?.startedAt)}.
          </Typography>
        </Box>
      )}

      {status.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {apiErrorMessage(status.error, 'Failed to load the report status')}
        </Alert>
      )}
      {run.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {apiErrorMessage(run.error, 'Failed to start the report')}
        </Alert>
      )}
      {data?.errorMessage && !isRunning && (
        <Alert severity="error" sx={{ mb: 2 }}>
          The last report failed: {data.errorMessage}
        </Alert>
      )}

      {!summary && !isRunning && !status.isLoading && (
        <Alert severity="info">
          No report yet. Running one reads every media record and lists the whole bucket, which can take a
          minute or two. Reports are kept until the Admin API next restarts.
        </Alert>
      )}

      {summary && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Generated {formatDateTime(summary.generatedAt)} in {summary.durationSeconds.toFixed(1)}s.
          </Typography>

          {!summary.bucketScanned && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              The bucket could not be listed{summary.bucketError ? `: ${summary.bucketError}` : ''}. Unused and
              library counts are still right; bucket, untracked and missing counts are not.
            </Alert>
          )}

          <ReportSummary summary={summary} />

          <ResponsiveTabs tabs={TABS} value={tab} onChange={setTab} sx={{ mb: 2 }} />

          {tab === 'unused' && (
            <ReportFilesTable
              view="unused"
              reportKey={reportKey}
              emptyMessage="Every content and system file is referenced somewhere."
            />
          )}
          {tab === 'library' && (
            <ReportFilesTable
              view="library"
              reportKey={reportKey}
              emptyMessage="No personal files are sitting unused in a library."
            />
          )}
          {tab === 'untracked' && <UntrackedObjectsTable prefixes={summary.untrackedByPrefix} reportKey={reportKey} />}
          {tab === 'missing' && (
            <ReportFilesTable
              view="missing"
              reportKey={reportKey}
              emptyMessage={summary.bucketScanned ? 'Every media record has its file.' : 'The bucket was not listed.'}
            />
          )}
          {tab === 'sources' && <ReferenceSourcesPanel summary={summary} />}
        </>
      )}
    </PageShell>
  )
}
